import {useState, useMemo, useRef} from 'react';
import styled from 'styled-components';
import {useVirtualizer} from '@tanstack/react-virtual';
import type {MentionUser} from '../hooks/useMentionUsers';
import type {BulkAssignResult} from '../hooks/useBulkAssign';

interface Props {
	ticketCount: number;
	users: MentionUser[];
	onConfirm: (userId: string | null) => void;
	onCancel: () => void;
	onDone?: () => void;
	loading?: boolean;
	results?: BulkAssignResult[];
}

const ITEM_HEIGHT = 36;
const MAX_VISIBLE = 8;

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 24rem;
	border-radius: 12px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;

const Title = styled.h2`
	margin: 0 0 16px;
	font-size: 18px;
	line-height: 28px;
	font-weight: 500;
	color: #0f172a;
`;

const ResultSummary = styled.p`
	margin: 0 0 8px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #334155;
`;

const FailureBox = styled.div`
	border-radius: 4px;
	border: 1px solid #fecaca;
	background: #fef2f2;
	padding: 12px;
`;

const FailureTitle = styled.p`
	margin: 0 0 4px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #991b1b;
`;

const FailureList = styled.ul`
	margin: 0;
	padding-left: 1.25em;
	list-style: disc inside;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #b91c1c;
`;

const SearchRow = styled.div`
	margin-bottom: 12px;
`;

const SearchInput = styled.input`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const OptionButton = styled.button`
	display: flex;
	height: 100%;
	width: 100%;
	align-items: center;
	gap: 8px;
	border: 0;
	border-radius: 4px;
	background: transparent;
	padding: 0 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #1e293b;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

const UnassignedButton = styled(OptionButton)`
	color: #64748b;
`;

const Avatar = styled.span`
	display: inline-flex;
	height: 24px;
	width: 24px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #e0e7ff;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #4338ca;
`;

const UserName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const LoadingBox = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 32px 0;
`;

const LoadingText = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #64748b;
`;

const Actions = styled.div`
	margin-top: 16px;
	display: flex;
	justify-content: flex-end;
`;

const DoneButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: #2563eb;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #1d4ed8;
	}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const CancelButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: transparent;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #475569;
	cursor: pointer;
	&:hover {
		background: #f1f5f9;
	}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

export function BulkAssignModal({ticketCount, users, onConfirm, onCancel, onDone, loading, results}: Props) {
	const [query, setQuery] = useState('');
	const scrollRef = useRef<HTMLDivElement>(null);

	const filtered = useMemo(() => {
		if (!query.trim()) return users;
		const lower = query.toLowerCase();
		return users.filter(u => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
	}, [users, query]);

	const virtualizer = useVirtualizer({
		count: filtered.length + 1,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ITEM_HEIGHT,
		overscan: 5,
	});

	const renderResults = () => {
		if (!results || results.length === 0) return null;
		const failures = results.filter(r => !r.success);
		const successCount = results.length - failures.length;
		return (
			<div>
				<ResultSummary>
					{successCount} ticket{successCount !== 1 ? 's' : ''} assigned successfully.
				</ResultSummary>
				{failures.length > 0 && (
					<FailureBox>
						<FailureTitle>
							{failures.length} ticket{failures.length !== 1 ? 's' : ''} failed:
						</FailureTitle>
						<FailureList>
							{failures.map(f => (
								<li key={f.ticketId}>
									{f.ticketId.slice(0, 8)}: {f.error}
								</li>
							))}
						</FailureList>
					</FailureBox>
				)}
				<Actions>
					<DoneButton type="button" onClick={onDone ?? onCancel}>
						Done
					</DoneButton>
				</Actions>
			</div>
		);
	};

	return (
		<Overlay>
			<Backdrop data-testid="modal-backdrop" onClick={onCancel} />
			<Dialog>
				<Title>
					Assign {ticketCount} ticket{ticketCount !== 1 ? 's' : ''}
				</Title>

				{results && results.length > 0 ? (
					renderResults()
				) : loading ? (
					<LoadingBox>
						<LoadingText>Assigning…</LoadingText>
					</LoadingBox>
				) : (
					<>
						<SearchRow>
							<SearchInput
								type="text"
								placeholder="Search users..."
								value={query}
								onChange={e => setQuery(e.target.value)}
							/>
						</SearchRow>
						<div
							ref={scrollRef}
							style={{
								height: Math.min((filtered.length + 1) * ITEM_HEIGHT, MAX_VISIBLE * ITEM_HEIGHT),
								overflow: 'auto',
							}}
						>
							<div style={{height: virtualizer.getTotalSize(), position: 'relative'}}>
								{virtualizer.getVirtualItems().map(virtualItem => {
									const index = virtualItem.index;
									if (index === 0) {
										return (
											<div
												key="unassigned"
												style={{
													position: 'absolute',
													top: 0,
													left: 0,
													width: '100%',
													height: ITEM_HEIGHT,
													transform: `translateY(${virtualItem.start}px)`,
												}}
											>
												<UnassignedButton type="button" onClick={() => onConfirm(null)}>
													Unassigned
												</UnassignedButton>
											</div>
										);
									}
									const user = filtered[index - 1]!;
									return (
										<div
											key={user.id}
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												height: ITEM_HEIGHT,
												transform: `translateY(${virtualItem.start}px)`,
											}}
										>
											<OptionButton type="button" onClick={() => onConfirm(user.id)}>
												<Avatar>
													{user.name
														.split(/\s+/)
														.map(p => p[0])
														.join('')
														.slice(0, 2)
														.toUpperCase()}
												</Avatar>
												<UserName>{user.name}</UserName>
											</OptionButton>
										</div>
									);
								})}
							</div>
						</div>
						<Actions>
							<CancelButton type="button" onClick={onCancel}>
								Cancel
							</CancelButton>
						</Actions>
					</>
				)}
			</Dialog>
		</Overlay>
	);
}
